import {
  HHChip,
  HHCollapsibleTable,
  HHStack,
  HHTypography,
} from '@hinge-health/react-component-library';
import TablePagination from '@mui/material/TablePagination';
import dayjs from 'dayjs';
import { useState } from 'react';
import { PackageType } from '../../types';
import { calculateChipColor } from '../../utils/contract-utils';
import { DiscountType, IDiscount } from '../../utils/discount-utils';
import LoadingComponent from '../utils/loading';
import DiscountDetailBase from './discount-type-display/discount-detail-base';
import ProgramAccessDetail from './discount-type-display/program-access';
import VolumeBasedDiscountsDetail from './discount-type-display/volume-based-discounts';
import BillingCapsUsage from './discount-usage-display/billing-caps';
import FreeBillableActivitiesUsage from './discount-usage-display/free-billable-activities';
import InnovationCreditsUsage from './discount-usage-display/innovation-credits';
import ProgramAccessUsage from './discount-usage-display/program-access';
import VolumeBasedDiscountsUsage from './discount-usage-display/volume-based-discounts';

export interface DiscountsListProps {
  discountsData?: IDiscount[];
  onEditDiscountClick?: (discountDefinitionId: number) => void;
  loading?: boolean;
  availablePackages: PackageType[];
  currency: string | null;
  total?: number;
  page?: number;
  pageSize?: number;
  onPageChange?: (newPage: number) => void;
  onPageSizeChange?: (newSize: number) => void;
}

const getDiscountDetailsComponent = (
  discountData: IDiscount,
  availablePackages: PackageType[],
  currency: string | null,
  handleEditDiscountClick?: (id: number) => void,
): JSX.Element => {
  const props = {
    discountData,
    availablePackages,
    currency,
    handleEditDiscountClick,
  };
  switch (discountData.discountType) {
    case DiscountType.programAccess:
      return <ProgramAccessDetail {...props} />;
    case DiscountType.volumeBasedDiscounts:
      return <VolumeBasedDiscountsDetail {...props} />;
    default:
      return <DiscountDetailBase {...props} />;
  }
};

const getDiscountUsageComponent = (
  discountData: IDiscount,
  currency: string | null,
): JSX.Element => {
  const props = {
    discountData,
    currency,
  };
  switch (discountData.discountType) {
    case DiscountType.billingCaps:
    case DiscountType.engagementBillingCaps:
      return <BillingCapsUsage {...props} />;
    case DiscountType.programAccess:
      return <ProgramAccessUsage {...props} />;
    case DiscountType.innovationCredits:
      return <InnovationCreditsUsage {...props} />;
    case DiscountType.volumeBasedDiscounts:
      return <VolumeBasedDiscountsUsage {...props} />;
    case DiscountType.freeBillableActivities:
    case DiscountType.programCapCredits:
      return <FreeBillableActivitiesUsage {...props} />;
    default:
      return <BillingCapsUsage {...props} />;
  }
};

const DiscountsList = ({
  discountsData,
  onEditDiscountClick,
  loading = true,
  availablePackages,
  currency,
  total,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
}: DiscountsListProps): JSX.Element => {
  const [openRowId, setOpenRowId] = useState<number | null>(null);

  const handleOpenRow =
    (id: number): React.MouseEventHandler<HTMLButtonElement> =>
    (): void => {
      if (openRowId !== id) {
        setOpenRowId(id);
      } else {
        setOpenRowId(null);
      }
    };

  return (
    <>
      <HHCollapsibleTable
        headers={[
          {
            align: 'left',
            headerTitle: 'Type',
          },
          {
            align: 'left',
            headerTitle: 'Details',
          },
          {
            align: 'left',
            headerTitle: 'Start Date',
          },
          {
            align: 'left',
            headerTitle: 'End Date',
          },
          {
            align: 'left',
            headerTitle: 'Status',
          },
        ]}
        emptyRowContent={
          loading ? (
            <LoadingComponent center />
          ) : (
            <HHStack direction="row" padding={4} justifyContent="center">
              <HHTypography hhVariant="muted">
                No associated discounts to display
              </HHTypography>
            </HHStack>
          )
        }
        rows={
          discountsData
            ? discountsData.map(discount => ({
                open: openRowId === discount.discountDefinitionId,
                setOpen: handleOpenRow(discount.discountDefinitionId),
                rowId: discount.discountDefinitionId,
                collapsibleRowContent: getDiscountDetailsComponent(
                  discount,
                  availablePackages,
                  currency,
                  onEditDiscountClick,
                ),
                columnStyles: {
                  textTransform: 'capitalize',
                },
                columnValues: [
                  {
                    columnItem: discount.discountType.replaceAll('_', ' '),
                  },
                  {
                    columnItem: getDiscountUsageComponent(discount, currency),
                  },
                  {
                    columnItem: dayjs(discount.startDate).format('L'),
                  },
                  {
                    columnItem: discount.endDate
                      ? dayjs(discount.endDate).format('L')
                      : '--',
                  },
                  {
                    columnItem: (
                      <HHStack direction="row" spacing={1}>
                        <HHChip
                          hhVariant="variant-bypass"
                          color={calculateChipColor(discount.status)}
                          size="small"
                          label={discount.status}
                        />
                      </HHStack>
                    ),
                  },
                ],
              }))
            : []
        }
      />
      {onPageChange && onPageSizeChange && (
        <HHStack direction="row" justifyContent="flex-end" alignItems="center">
          <TablePagination
            component="div"
            count={total ?? 0}
            page={(page ?? 1) - 1}
            onPageChange={(_event, newPage): void => {
              onPageChange(newPage + 1);
            }}
            rowsPerPage={pageSize ?? 5}
            onRowsPerPageChange={(event): void => {
              onPageSizeChange(parseInt(event.target.value, 10));
            }}
            rowsPerPageOptions={[5, 10, 20]}
            labelRowsPerPage="Discounts per page:"
          />
        </HHStack>
      )}
    </>
  );
};

export default DiscountsList;
